import { useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronRight, Clock, XCircle } from "lucide-react";
import { useSystemHealthGroupBooking, useSystemHealthGroupBookings, useSystemHealthGroups } from "../../lib/api";
import {
  Badge, Chip, Crumbs, FailedToLoad, Heading, IconChip, Loading, Notice, PAGE, Pager, ScrollHint, Segmented,
  dangerButton, endpoint, focusRing, panel, pillClass, pillStyle, primaryButton, td, th,
} from "./healthUi";
import {
  GROUP_STEPS, STEP_STATE_LABELS, formatMs, formatSeconds, formatWhen, outcomeLabel, outcomeTone, ownerLabel,
  stateStyle, stepLabel, stepStateLabel, stepStateTone,
} from "./healthFormat";

// Group creation tab: the four steps a booking goes through, why bookings stop
// at each one, the bookings behind every number, and one booking in full.
//   the four steps -> the bookings behind a number -> one booking

const OUTCOME_FILTERS = [
  { key: "", label: "All" },
  { key: "created", label: "Group created" },
  { key: "failed", label: "Failed" },
  { key: "stuck", label: "Stuck" },
];

const HEALTH_VIEW = { level: "health" };

// One mark per state, each a different shape, so colour is never the only signal.
const GLYPH_INK = { ok: "#16A34A", failed: "#DC2626", stuck: "#B45309", quiet: "#6B7280" };

function StepGlyph({ state }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" className="flex-none">
      {state === "ok" && <circle cx="7" cy="7" r="5.5" fill={GLYPH_INK.ok} />}
      {state === "failed" && (
        <>
          <circle cx="7" cy="7" r="6" fill={GLYPH_INK.failed} />
          <path d="M4.6 4.6l4.8 4.8M9.4 4.6L4.6 9.4" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        </>
      )}
      {state === "stuck" && (
        <>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke={GLYPH_INK.stuck} strokeWidth="1.5" />
          <path d="M7 1.5a5.5 5.5 0 000 11z" fill={GLYPH_INK.stuck} />
        </>
      )}
      {state === "waiting" && <circle cx="7" cy="7" r="5.2" fill="none" stroke={GLYPH_INK.quiet} strokeWidth="1.5" />}
      {state === "unknown" && (
        <circle cx="7" cy="7" r="5.2" fill="none" stroke={GLYPH_INK.quiet} strokeWidth="1.5" strokeDasharray="2.2 2" />
      )}
      {(state === "skipped" || !state) && <path d="M3.5 7h7" stroke={GLYPH_INK.quiet} strokeWidth="1.6" strokeLinecap="round" />}
    </svg>
  );
}

// Four marks, one per step, so a list of bookings shows where each one stopped.
function StepDots({ steps }) {
  const spoken = GROUP_STEPS.map((s) => `${s.label}: ${stepStateLabel(steps?.[s.key])}`).join(", ");
  return (
    <span role="img" aria-label={spoken} title={spoken} className="inline-flex items-center gap-1.5">
      {GROUP_STEPS.map((s) => (
        <StepGlyph key={s.key} state={steps?.[s.key]} />
      ))}
    </span>
  );
}

const LEGEND = ["ok", "failed", "stuck", "waiting", "skipped", "unknown"];

function StepLegend() {
  return (
    <ul aria-label="What the marks mean" className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[#4B5563]">
      {LEGEND.map((state) => (
        <li key={state} className="flex items-center gap-1.5">
          <StepGlyph state={state} />
          {STEP_STATE_LABELS[state]}
        </li>
      ))}
    </ul>
  );
}

// ---- screen 1: the four steps -----------------------------------------------

const EMPTY_STEP = { ok: 0, failed: 0, stuck: 0, waiting: 0, skipped: 0, unknown: 0, reasons: [] };

const surface = panel;
const OUTCOME_ICONS = { good: CheckCircle2, bad: XCircle, warn: AlertTriangle, idle: Clock };

// Each booking stops at one step at most (later steps are not reached), so
// adding the steps up counts every booking once.
function attentionOf(steps) {
  const failed = steps.reduce((n, s) => n + (s.failed || 0), 0);
  const stuck = steps.reduce((n, s) => n + (s.stuck || 0), 0);
  return { failed, stuck, total: failed + stuck };
}

function StepColumn({ step, index, data, open }) {
  const n = (key) => data[key] || 0;
  const reached = n("ok") + n("failed") + n("stuck") + n("waiting") + n("unknown");
  const problems = n("failed") + n("stuck");
  const finished = n("ok") + n("unknown");
  const goStep = () => open({ level: "bookings", step: step.key, label: `${step.label}: failed or stuck` });
  return (
    <section aria-label={step.label} title={step.help} className="flex min-w-0 flex-col p-5">
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="flex h-6 w-6 flex-none items-center justify-center rounded-full text-[11px] font-semibold text-white"
          style={{ background: problems ? "#DC2626" : "#2667B5" }}
        >
          {index + 1}
        </span>
        <h3 className="text-[14px] font-semibold text-[#111827]">{step.label}</h3>
      </div>
      {problems > 0 ? (
        <>
          <button
            type="button"
            onClick={goStep}
            aria-label={`${problems} bookings failed or stuck at ${step.label}`}
            className={`mt-3 w-fit cursor-pointer rounded text-left text-[26px] font-semibold leading-tight tabular-nums text-[#B91C1C] underline-offset-4 hover:underline ${focusRing}`}
          >
            {problems}
          </button>
          <span className="text-[13px] font-medium text-[#B91C1C]">failed or stuck</span>
          <span className="text-[12px] text-[#6B7280]">{finished} of {reached} finished</span>
        </>
      ) : (
        <>
          <span className="mt-3 text-[26px] font-semibold leading-tight tabular-nums text-[#111827]">{finished}</span>
          <span className="text-[13px] text-[#6B7280]">{reached ? "finished" : "nothing reached this step"}</span>
        </>
      )}
      {n("waiting") > 0 && <span className="text-[12px] text-[#6B7280]">{n("waiting")} in progress</span>}
      {reached > 0 && (
        <span aria-hidden="true" className="mt-3.5 flex h-1.5 w-full overflow-hidden rounded-full bg-[#F3F4F6]">
          <span style={{ width: `${(100 * finished) / reached}%`, background: "#16A34A" }} />
          <span style={{ width: `${(100 * n("waiting")) / reached}%`, background: "#CBD5E1" }} />
          <span style={{ width: `${(100 * problems) / reached}%`, background: "#DC2626" }} />
        </span>
      )}
    </section>
  );
}

// "Problems": every reason across the four steps in one plain list.
function WhatWentWrong({ steps, open }) {
  const rows = steps.flatMap((s) => (s.reasons || []).map((r) => ({ ...r, step: s.key, stepLabel: s.label })));
  if (!rows.length) return null;
  return (
    <section className={`${surface} p-5 sm:p-6`}>
      <h2 className="text-[17px] font-semibold text-[#111827]">Problems</h2>
      <ul className="mt-2 divide-y divide-[#F3F4F6]">
        {rows.map((r) => (
          <li key={`${r.step}-${r.key}`}>
            <button
              type="button"
              onClick={() => open({ level: "bookings", step: r.step, reason: r.key, label: `${r.stepLabel}: ${r.label}` })}
              aria-label={`${r.count} bookings: ${r.label}`}
              className={`-mx-2 flex min-h-12 w-[calc(100%+16px)] cursor-pointer items-center gap-4 rounded-xl px-2 py-3 text-left hover:bg-[#F9FAFB] ${focusRing}`}
            >
              <IconChip icon={AlertTriangle} tone="bad" size={9} />
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold text-[#111827]">{r.label}</span>
                <span className="block text-[12px] text-[#6B7280]">{r.stepLabel} · Who fixes it: {ownerLabel(r.owner)}</span>
              </span>
              <Chip tone="bad">{r.count}</Chip>
              <ChevronRight size={16} aria-hidden="true" className="flex-none text-[#9CA3AF]" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CallHealth({ api }) {
  if (api == null) {
    return <p className="mt-3 text-[13px] text-[#6B7280]">The Chat360 call numbers could not be read right now. The bookings above are not affected.</p>;
  }
  const rows = [
    { label: "Create group", calls: api.calls, failed: api.failed, p50: api.p50_ms, p95: api.p95_ms },
    { label: "List groups", calls: api.list?.calls ?? 0, failed: api.list?.failed ?? 0, p50: api.list?.p50_ms, p95: api.list?.p95_ms },
    { label: "Send invitation", calls: api.invites?.calls ?? 0, failed: api.invites?.failed ?? 0, p50: api.invites?.p50_ms, p95: api.invites?.p95_ms },
    { label: "Login", calls: api.login_calls, failed: api.login_failed, p50: null, p95: null },
  ];
  if (!rows.some((r) => r.calls)) {
    return <p className="mt-3 text-[13px] text-[#6B7280]">No Chat360 calls for group creation were recorded in this period.</p>;
  }
  return (
    <>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[480px] border-collapse">
          <thead>
            <tr>
              <th className={th}>Call</th>
              {["Calls", "Failed", "Typical time", "Slowest 1 in 20"].map((h) => (
                <th key={h} className={`${th} text-right`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-t border-[#F3F4F6]">
                <td className={td}>{r.label}</td>
                <td className={`${td} text-right font-mono tabular-nums`}>{r.calls}</td>
                <td className={`${td} text-right font-mono tabular-nums ${r.failed ? "font-semibold text-[#B91C1C]" : ""}`}>{r.failed}</td>
                <td className={`${td} text-right font-mono tabular-nums`}>{formatMs(r.p50)}</td>
                <td className={`${td} text-right font-mono tabular-nums`}>{formatMs(r.p95)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ScrollHint />
    </>
  );
}

function Health({ query, open }) {
  const { data, isLoading, error, refetch } = query;
  if (isLoading) return <Loading />;
  if (error) return <FailedToLoad error={error} retry={refetch} />;
  const { report, api } = data;
  const t = report.totals;
  const anyCalls = api && (api.calls || api.login_calls || api.list?.calls || api.invites?.calls);
  if (!t.booked && !anyCalls) {
    return <Notice>No services were booked and no groups were created in this period. Try a longer period.</Notice>;
  }
  const steps = report.steps || [];
  const byKey = Object.fromEntries(steps.map((s) => [s.key, s]));
  const { failed, stuck, total } = attentionOf(steps);
  const people = report.people;
  const unknown = byKey.invites?.unknown || 0;
  const headline = total
    ? `${total} ${total === 1 ? "booking needs" : "bookings need"} attention`
    : t.booked
      ? `All ${t.booked} ${t.booked === 1 ? "booking" : "bookings"} went through`
      : "No bookings in this period";
  const detail = total
    ? [failed && `${failed} failed`, stuck && `${stuck} stuck`].filter(Boolean).join(", ") + ` · ${t.booked} bookings in this period`
    : "Nothing needs attention in this period.";
  return (
    <div title={endpoint("groups")} className="mt-6 flex flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div role="status" className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
          {total ? (
            <AlertTriangle size={18} aria-hidden="true" className="flex-none text-[#DC2626]" />
          ) : (
            <CheckCircle2 size={18} aria-hidden="true" className="flex-none text-[#16A34A]" />
          )}
          <Heading className="text-[15px] font-semibold text-[#111827]">{headline}</Heading>
          <span className="text-[13px] text-[#6B7280]">{detail}</span>
        </div>
        <div className="flex items-center gap-2">
          {total > 0 && (
            <button
              type="button"
              onClick={() => open({ level: "bookings", attention: true, label: "Needs attention" })}
              className={dangerButton}
            >
              Show them
            </button>
          )}
          <button
            type="button"
            onClick={() => open({ level: "bookings", label: "All bookings" })}
            className={primaryButton}
          >
            See all bookings
          </button>
        </div>
      </header>

      <div className={`${surface} grid divide-y divide-[#F3F4F6] overflow-hidden sm:grid-cols-2 sm:divide-y-0 xl:grid-cols-4 xl:divide-x`}>
        {GROUP_STEPS.map((step, index) => (
          <StepColumn key={step.key} step={step} index={index} data={byKey[step.key] || { ...EMPTY_STEP }} open={open} />
        ))}
      </div>

      {people?.tracked && (people.sent > 0 || people.failed > 0) && (
        <p className="px-1 text-[13px] text-[#6B7280]">
          {people.sent} {people.sent === 1 ? "person was" : "people were"} sent an invitation
          {people.failed > 0 ? `, ${people.failed} not sent` : ""}.
        </p>
      )}
      {people && !people.tracked && (
        <p className="px-1 text-[13px] text-[#6B7280]">Results for each person could not be read right now. Each booking's invitation step is judged from the booking alone.</p>
      )}
      {unknown > 0 && (
        <p className="px-1 text-[12px] text-[#6B7280]">
          {unknown} {unknown === 1 ? "booking has" : "bookings have"} no record of who was invited, usually because {unknown === 1 ? "it was" : "they were"} made before this was tracked.
        </p>
      )}

      <WhatWentWrong steps={steps} open={open} />

      {report.on_fallback > 0 && (
        <p className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-[13px] text-[#92400E]">
          <strong className="font-semibold">
            {report.on_fallback} {report.on_fallback === 1 ? "group was" : "groups were"} made on the fallback number
          </strong>{" "}
          because Chat360 refused the main number. The groups work, but the main number needs a look. They are marked "Fallback number" in the list.
        </p>
      )}

      {report.possible_duplicates > 0 && (
        <p className="px-1 text-[13px] text-[#92400E]">
          {report.possible_duplicates} bookings share a group name and a customer with another booking. They may be repeats and are marked "Possible duplicate" in the list.
        </p>
      )}

      <details className={`${surface} group px-5 py-4 sm:px-6`}>
        <summary className={`flex min-h-10 cursor-pointer list-none items-center justify-between rounded-lg text-[15px] font-semibold text-[#111827] ${focusRing}`}>
          Chat360 calls
          <span aria-hidden="true" className="text-[12px] font-medium text-[#6B7280] group-open:hidden">Show</span>
        </summary>
        <CallHealth api={api} />
        {report.seconds_p50 != null && (
          <p className="mt-3 text-[12px] text-[#6B7280]">
            From booking to a ready group: typically {formatSeconds(report.seconds_p50)}, slowest 1 in 20 {formatSeconds(report.seconds_p95)} ({report.timed} {report.timed === 1 ? "group" : "groups"} timed).
          </p>
        )}
      </details>
    </div>
  );
}

// ---- screen 2: the bookings -------------------------------------------------

function Bookings({ scope, range, view, open }) {
  const [outcome, setOutcome] = useState(view.outcome || "");
  const [reason, setReason] = useState(view.reason || "");
  const [stepFilter, setStepFilter] = useState(view.step ? { step: view.step, state: view.state || "" } : null);
  const [attention, setAttention] = useState(Boolean(view.attention));
  const [page, setPage] = useState(view.page || 0);
  const { data, isLoading, error, refetch } = useSystemHealthGroupBookings({
    ...scope, from: range.from(), outcome, reason, attention,
    step: stepFilter?.step, state: stepFilter?.state, limit: PAGE, offset: page * PAGE,
  });
  // Choosing a result filter starts a fresh list, so a step or reason picked
  // earlier is dropped with it rather than silently combined.
  const pick = (key) => {
    setOutcome(key);
    setReason("");
    setStepFilter(null);
    setAttention(false);
    setPage(0);
  };
  const filtered = Boolean(reason || stepFilter || attention);
  const chip = (label, clear, name) => (
    <button type="button" onClick={clear} aria-label={`Remove the filter: ${name}`} className={pillClass} style={pillStyle(true)}>
      {label} ×
    </button>
  );
  const back = { ...view, outcome, reason, step: stepFilter?.step, state: stepFilter?.state, attention, page };
  const result = (row) => (
    <>
      <span className="block text-[13px] font-medium text-[#111827]">{row.stopped_at ? `Stopped at ${stepLabel(row.stopped_at)}` : "Finished"}</span>
      {row.reason && <span className="block text-[12px] text-[#6B7280]">{row.reason}</span>}
      {(row.invites?.failed > 0) && <span className="block text-[12px] font-medium text-[#B91C1C]">{row.invites.failed} not sent</span>}
    </>
  );
  return (
    <section title={endpoint("group-bookings")} className={`${surface} mt-4 p-5 sm:p-6`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[12px] font-medium uppercase tracking-[0.06em] text-[#6B7280]">Bookings</p>
          <Heading className="mt-0.5 text-[18px] font-semibold text-[#111827]">{view.label || "All bookings"}</Heading>
          {data && <p className="mt-0.5 text-[13px] text-[#6B7280]">{data.total} {data.total === 1 ? "booking" : "bookings"}</p>}
        </div>
        <Segmented label="Show" options={OUTCOME_FILTERS} isOn={(key) => outcome === key && !filtered} pick={pick} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 empty:hidden">
        {attention && chip("Needs attention", () => { setAttention(false); setPage(0); }, "Needs attention")}
        {stepFilter && chip(
          `Step: ${stepLabel(stepFilter.step)}${stepFilter.state ? `, ${stepStateLabel(stepFilter.state).toLowerCase()}` : ", failed or stuck"}`,
          () => { setStepFilter(null); setReason(""); setPage(0); },
          stepLabel(stepFilter.step),
        )}
        {reason && chip(`Reason: ${view.label}`, () => { setReason(""); setPage(0); }, view.label)}
      </div>
      <div aria-live="polite">
        {isLoading ? (
          <Loading />
        ) : error ? (
          <FailedToLoad error={error} retry={refetch} />
        ) : data.total === 0 ? (
          <p className="mt-4 text-[13px] text-[#6B7280]">
            No bookings match these filters.{" "}
            <button type="button" onClick={() => pick("")} className={`cursor-pointer rounded font-semibold text-[#2667B5] underline ${focusRing}`}>
              Show all bookings
            </button>
          </p>
        ) : (
          <>
            <StepLegend />
            <ul aria-label="Bookings" className="mt-3 divide-y divide-[#F3F4F6] border-t border-[#F3F4F6]">
              {data.rows.map((row) => {
                const tone = outcomeTone(row.outcome);
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => open({ level: "booking", id: row.id, from: back })}
                      className={`-mx-2 flex w-[calc(100%+16px)] cursor-pointer items-center gap-4 rounded-xl px-2 py-3.5 text-left hover:bg-[#F9FAFB] ${focusRing}`}
                    >
                      <IconChip icon={OUTCOME_ICONS[tone] || Clock} tone={tone} />
                      <span className="min-w-0 flex-1">
                        <span className="block break-words text-[14px] font-semibold text-[#111827]">{row.name || "Open"}</span>
                        <span className="block text-[12px] text-[#6B7280]">{[row.workshop, formatWhen(row.booked_at)].filter(Boolean).join(" · ")}</span>
                        <span className="mt-1 block">{result(row)}</span>
                        {row.possible_duplicate && <span className="mt-0.5 block text-[11px] font-medium text-[#92400E]">Possible duplicate</span>}
                        {row.on_fallback && <span className="mt-0.5 block text-[11px] font-medium text-[#92400E]">Fallback number</span>}
                      </span>
                      <span className="flex flex-none flex-col items-end gap-1.5">
                        <StepDots steps={row.steps} />
                        {row.seconds_to_create != null && (
                          <span className="text-[12px] tabular-nums text-[#6B7280]">ready in {formatSeconds(row.seconds_to_create)}</span>
                        )}
                      </span>
                      <ChevronRight size={16} aria-hidden="true" className="hidden flex-none text-[#9CA3AF] sm:block" />
                    </button>
                  </li>
                );
              })}
            </ul>
            <Pager page={page} shown={data.rows.length} total={data.total} onPage={setPage} />
          </>
        )}
      </div>
    </section>
  );
}

// ---- screen 3: one booking --------------------------------------------------

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6B7280]">{label}</div>
      <div className="mt-0.5 text-[13px] text-[#111827]">{children || "–"}</div>
    </div>
  );
}

function StepStrip({ steps }) {
  return (
    <ol className="grid gap-2 sm:grid-cols-4" aria-label="The four steps">
      {GROUP_STEPS.map((s, i) => {
        const state = steps?.[s.key];
        const style = stateStyle(stepStateTone(state));
        return (
          <li key={s.key} className="rounded-lg border bg-white p-3" style={{ borderColor: state === "failed" || state === "stuck" ? style.border : "#E5E7EB" }}>
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6B7280]">Step {i + 1}</div>
            <div className="mt-0.5 text-[13px] font-semibold text-[#111827]">{s.label}</div>
            <div className="mt-1.5 flex items-center gap-1.5 text-[12px] font-medium" style={{ color: style.text }}>
              <StepGlyph state={state} />
              {stepStateLabel(state)}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function People({ booking }) {
  if (!booking.people_tracked && !booking.people?.length) {
    return <p className="mt-3 text-[13px] text-[#4B5563]">Who was invited could not be read for this booking right now.</p>;
  }
  if (!booking.people?.length) {
    return <p className="mt-3 text-[13px] text-[#4B5563]">No record of who was invited. This usually means the booking was made before invitations were tracked.</p>;
  }
  return (
    <>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse">
          <thead>
            <tr className="border-b border-[#F0F1F3]">
              <th className={th}>Who</th>
              <th className={th}>Number ending</th>
              <th className={th}>Result</th>
              <th className={th}>Reason</th>
              <th className={`${th} text-right`}>Tries</th>
            </tr>
          </thead>
          <tbody>
            {booking.people.map((p) => (
              <tr key={`${p.who}-${p.last4}`} className="border-b border-[#F0F1F3] last:border-0">
                <td className={td}>{p.who}</td>
                <td className={`${td} font-mono tabular-nums`}>{p.last4 || "–"}</td>
                <td className={td}><Badge label={p.sent ? "Sent" : "Not sent"} tone={p.sent ? "good" : "bad"} /></td>
                <td className={`${td} text-[12px]`}>
                  {p.reason ? (
                    <>
                      {p.reason.label}
                      <span className="block text-[11px] text-[#6B7280]">Who fixes it: {ownerLabel(p.reason.owner)}. {p.reason.next_step}</span>
                      {p.error_text && <span className="mt-0.5 block break-words font-mono text-[11px] text-[#4B5563]">{p.error_text}</span>}
                    </>
                  ) : (
                    <span className="text-[#6B7280]">–</span>
                  )}
                </td>
                <td className={`${td} text-right font-mono tabular-nums`}>{p.tries}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ScrollHint />
    </>
  );
}

function Booking({ id }) {
  const { data: booking, isLoading, error, refetch } = useSystemHealthGroupBooking(id);
  if (isLoading) return <Loading />;
  if (error) return <FailedToLoad error={error} retry={refetch} />;
  const failure = booking.failure;
  const stuckAt = booking.stopped_at ? stepLabel(booking.stopped_at) : "";
  return (
    <div title={endpoint(`group-bookings/${id}`)} className="mt-4 flex flex-col gap-5">
      <section className={`${surface} p-6`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Heading className="text-[18px] font-semibold text-[#111827]">{booking.name || "Booking"}</Heading>
          <Chip tone={outcomeTone(booking.outcome)}>{outcomeLabel(booking.outcome)}</Chip>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Booked at">{formatWhen(booking.booked_at)}</Field>
          <Field label="Workshop">{booking.workshop}</Field>
          <Field label="Time to create">{booking.seconds_to_create != null ? formatSeconds(booking.seconds_to_create) : ""}</Field>
          <Field label="Sent from number ending">{booking.from_number ? <span className="font-mono tabular-nums">{booking.from_number}</span> : ""}</Field>
        </div>
        {booking.on_fallback && (
          <p className="mt-4 text-[12px] font-medium text-[#92400E]">
            Made on the fallback number: Chat360 refused the main number for this booking, so every message in this group comes from the fallback number.
          </p>
        )}
        {booking.possible_duplicate && (
          <p className="mt-4 text-[12px] font-medium text-[#92400E]">Possible duplicate: another booking has the same group name and customer.</p>
        )}
        <div className="mt-5">
          <StepStrip steps={booking.steps} />
        </div>
      </section>

      {failure && (
        <section className="rounded-2xl border border-[#FECACA] bg-[#FEF2F2] p-5 sm:p-6">
          <h2 className="text-[16px] font-semibold text-[#991B1B]">{stuckAt ? `Stopped at ${stuckAt}` : "Problems"}</h2>
          <p className="mt-2 text-[14px] text-[#111827]">{failure.label}</p>
          <p className="mt-2 text-[13px] text-[#374151]">
            <strong>Who fixes it:</strong> {ownerLabel(failure.owner)}. {failure.next_step}
          </p>
          {booking.raw_error && <p className="mt-3 break-words font-mono text-[11px] text-[#4B5563]">{booking.raw_error}</p>}
        </section>
      )}

      <section className={`${surface} p-6`}>
        <h2 className="text-[16px] font-semibold text-[#111827]">Invitations</h2>
        <People booking={booking} />
      </section>

      <section className={`${surface} p-6`}>
        <h2 className="text-[16px] font-semibold text-[#111827]">Timeline</h2>
        <ol className="mt-4 flex flex-col gap-3">
          {booking.timeline.map((step, i) => (
            <li key={`${step.what}-${i}`} className="flex items-start gap-3 text-[13px]">
              <span className="mt-1.5 h-2 w-2 flex-none rounded-full" style={{ background: step.ok ? "#16A34A" : "#DC2626" }} />
              <span className="w-[90px] flex-none text-[#4B5563] sm:w-[110px]">{step.at ? formatWhen(step.at) : ""}</span>
              <span className="min-w-0 text-[#111827]">
                {step.what}
                {!step.ok && <span className="ml-2 font-semibold text-[#B91C1C]">Problem</span>}
              </span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

// ---- the tab ----------------------------------------------------------------

export default function HealthGroups({ scope, range }) {
  const [view, setView] = useState(HEALTH_VIEW);
  const query = useSystemHealthGroups({ ...scope, from: range.from() });

  // Each crumb goes back to the screen it names, so the trail is the back button.
  const at = view.level === "booking" ? view.from : view;
  const toHealth = () => setView(HEALTH_VIEW);
  const trail = [{ label: "Group creation", go: view.level === "health" ? undefined : toHealth }];
  if (view.level === "bookings") trail.push({ label: view.label || "All bookings" });
  if (view.level === "booking") {
    trail.push({ label: at.label || "All bookings", go: () => setView(view.from) });
    trail.push({ label: "One booking" });
  }

  return (
    <>
      <Crumbs trail={trail} period={range.label} />
      {view.level === "health" && <Health query={query} open={setView} />}
      {view.level === "bookings" && <Bookings key={JSON.stringify(view)} scope={scope} range={range} view={view} open={setView} />}
      {view.level === "booking" && <Booking id={view.id} />}
    </>
  );
}
